import { useState } from 'react';
import { useStats, type StatsTimeframe } from '../hooks/useHatMail';

const TIMEFRAMES: { value: StatsTimeframe; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

function trendArrow(trend: 'up' | 'down' | 'flat'): string {
  if (trend === 'up') return '\u2191';
  if (trend === 'down') return '\u2193';
  return '\u2192';
}

function trendColor(trend: 'up' | 'down' | 'flat'): string {
  // For response time, down is good
  if (trend === 'down') return 'text-emerald-500';
  if (trend === 'up') return 'text-red-500';
  return 'text-gray-400';
}

function percent(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 100);
}

export default function StatsView() {
  const [timeframe, setTimeframe] = useState<StatsTimeframe>('today');
  const { data: stats, isLoading, error } = useStats(timeframe);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-ghw-gold" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="rounded-xl bg-red-50 p-6 text-center">
        <p className="text-sm font-medium text-red-700">Could not load stats</p>
        {error && <p className="mt-1 text-xs text-red-500">{(error as Error).message}</p>}
      </div>
    );
  }

  const maxCategory = Math.max(...Object.values(stats.category_breakdown), 1);
  const context = stats.order_context_coverage;
  const allContext = stats.order_context_all_time;

  return (
    <div>
      {/* Timeframe toggle */}
      <div className="mb-6 flex items-center gap-1 rounded-xl bg-gray-100 p-1">
        {TIMEFRAMES.map((tf) => (
          <button
            key={tf.value}
            onClick={() => setTimeframe(tf.value)}
            className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
              timeframe === tf.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={{ minHeight: '36px' }}
          >
            {tf.label}
          </button>
        ))}
      </div>

      {/* Stat Cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Response Time */}
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Avg Response</p>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900">
              {stats.response_time_avg}
            </span>
            <span className="text-sm text-gray-400">min</span>
            <span className={`ml-auto text-lg ${trendColor(stats.response_time_trend)}`}>
              {trendArrow(stats.response_time_trend)}
            </span>
          </div>
        </div>

        {/* Volume */}
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Volume</p>
          <div className="mt-1">
            <span className="text-2xl font-bold text-gray-900">{stats.volume[timeframe]}</span>
          </div>
          <p className="mt-0.5 text-xs text-gray-400">
            {stats.volume.today}d / {stats.volume.week}w / {stats.volume.month}m
          </p>
        </div>

        {/* Rubber Stamp Rate */}
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rubber Stamp</p>
          <div className="mt-1">
            <span className="text-2xl font-bold text-gray-900">
              {Math.round(stats.rubber_stamp_rate)}%
            </span>
          </div>
          <p className="mt-0.5 text-xs text-gray-400">
            sent without edits
          </p>
        </div>

        {/* Escalations */}
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Escalations</p>
          <div className="mt-1">
            <span className="text-2xl font-bold text-gray-900">{stats.escalation_count}</span>
          </div>
          <p className="mt-0.5 text-xs text-gray-400">this period</p>
        </div>
      </div>

      {/* Order Context Coverage */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Order Context</h3>
          <span className="text-xs font-medium text-gray-400">
            {percent(context.stage_found, context.prod_status_total)}% verified
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">PROD Status</p>
            <p className="mt-1 text-xl font-bold text-gray-900">{context.prod_status_total}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Snapshots</p>
            <p className="mt-1 text-xl font-bold text-gray-900">{context.snapshots_total}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Stage Found</p>
            <p className="mt-1 text-xl font-bold text-emerald-700">{context.stage_found}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Missing Context</p>
            <p className="mt-1 text-xl font-bold text-amber-700">{context.missing_verified_context}</p>
          </div>
        </div>
        <div className="mt-3 grid gap-2 text-xs text-gray-600 sm:grid-cols-2">
          <p>Customer matched: <span className="font-medium text-gray-900">{context.customer_matched}</span></p>
          <p>Order matched: <span className="font-medium text-gray-900">{context.order_matched}</span></p>
          <p>Stage date known: <span className="font-medium text-gray-900">{context.stage_entry_date_known}</span></p>
          <p>Team review flags: <span className="font-medium text-gray-900">{context.anomaly_escalated}</span></p>
          <p>Approved or sent: <span className="font-medium text-gray-900">{context.approved + context.sent}</span></p>
          <p>All-time verified: <span className="font-medium text-gray-900">{allContext.stage_found}/{allContext.prod_status_total}</span></p>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">By Category</h3>
        <div className="space-y-2">
          {Object.entries(stats.category_breakdown).map(([cat, count]) => (
            <div key={cat} className="flex items-center gap-3">
              <span className="w-28 truncate text-xs text-gray-600">
                {cat.replace(/_/g, ' ')}
              </span>
              <div className="flex-1">
                <div className="h-5 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-ghw-gold/70 transition-all duration-300"
                    style={{ width: `${(count / maxCategory) * 100}%` }}
                  />
                </div>
              </div>
              <span className="w-8 text-right text-xs font-medium text-gray-700">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-Person Activity */}
      {stats.per_person.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Per Person</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs text-gray-500">
                  <th className="pb-2 pr-4 text-left font-medium">Name</th>
                  <th className="pb-2 px-2 text-right font-medium">Sent</th>
                  <th className="pb-2 px-2 text-right font-medium">Edited</th>
                  <th className="pb-2 pl-2 text-right font-medium">Escalated</th>
                </tr>
              </thead>
              <tbody>
                {stats.per_person.map((person) => (
                  <tr key={person.name} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-800">{person.name}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{person.sent}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{person.edited}</td>
                    <td className="py-2 pl-2 text-right text-gray-600">{person.escalated}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
